import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Dialog as DialogPrimitive } from "radix-ui";

import { api, ApiError } from "@/lib/api";
import { useTranslations } from "@/i18n/IntlProvider";
import { Button } from "@/components/ui/button";
import type { MeResponse } from "@/auth/me";
import { useRouteContext } from "@tanstack/react-router";

/**
 * /$locale/settings/account — profile (read-only) + DSGVO self-service.
 *
 * Endpoint contract (verified against
 *   modules/identity/.../api/AccountSelfController.kt and
 *   modules/identity/.../api/AccountLifecycleController.kt):
 *
 *   GET  /v1/accounts/me              — profile (mirrored Keycloak data)
 *   POST /v1/accounts/export          — 202 + LifecycleRequest body
 *   POST /v1/accounts/delete          — 202 + LifecycleRequest body
 *   POST /v1/accounts/delete/cancel   — 200 + LifecycleRequest body
 *   GET  /v1/accounts/lifecycle       — 10 newest requests
 *
 * The display name is intentionally not editable here — its source of
 * truth is Keycloak (ADR-011); the AccountSelfController exposes no
 * PUT endpoint. The legacy web/ profile panel makes the same choice
 * and links the user to the Keycloak account console for edits.
 */

type AccountSelfResponse = {
  id: string;
  displayName: string;
  primaryEmail: string;
  createdAt: string;
  archived: boolean;
};

type LifecycleKind = "DELETE" | "EXPORT";
type LifecycleStatus = "PENDING" | "CANCELLED" | "COMPLETED" | "FAILED";

type LifecycleRequest = {
  id: string;
  kind: LifecycleKind;
  status: LifecycleStatus;
  requestedBy: string;
  requestedAt: string;
  purgeAfter: string | null;
  completedAt: string | null;
};

export function SettingsAccountRoute() {
  const t = useTranslations();
  const ctx = useRouteContext({ strict: false }) as { me?: MeResponse | null };
  const principal = ctx?.me ?? null;

  return (
    <section className="mx-auto max-w-screen-md space-y-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("app.account.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("app.account.subtitle")}
        </p>
      </header>

      <ProfileSection principal={principal} />
      <GdprSection />
    </section>
  );
}

/* ---------- Profile ----------------------------------------------------- */

function ProfileSection({ principal }: { principal: MeResponse | null }) {
  const t = useTranslations();

  // GET /v1/accounts/me is the qrody-side mirror. The MeResponse from
  // /auth/me already carries the same fields, so we use that for the
  // first paint and fall back to the qrody copy only if the BFF
  // probe was unauthenticated by the time this component mounts.
  const profile = useQuery({
    queryKey: ["account", "me"],
    queryFn: () => api<AccountSelfResponse>("/v1/accounts/me"),
    enabled: principal != null,
    retry: false,
  });

  const displayName =
    profile.data?.displayName ?? principal?.name ?? principal?.email ?? "—";
  const email = profile.data?.primaryEmail ?? principal?.email ?? "—";

  return (
    <section
      aria-labelledby="account-profile-heading"
      className="rounded-lg border border-border bg-card p-4 sm:p-6"
    >
      <h2
        id="account-profile-heading"
        className="text-lg font-semibold tracking-tight"
      >
        {t("app.account.profile.title")}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {t("app.account.profile.desc")}
      </p>
      <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label={t("app.account.profile.displayName")}
          value={displayName}
        />
        <Field label={t("app.account.profile.email")} value={email} />
      </dl>
      {profile.isError ? (
        <p className="mt-4 text-sm text-destructive">
          {t("common.error")}: {describeError(profile.error)}
        </p>
      ) : null}
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm">{value}</dd>
    </div>
  );
}

/* ---------- GDPR -------------------------------------------------------- */

function GdprSection() {
  const t = useTranslations();
  const qc = useQueryClient();

  const history = useQuery({
    queryKey: ["lifecycle"],
    queryFn: () => api<LifecycleRequest[]>("/v1/accounts/lifecycle"),
    retry: false,
  });

  const exportMut = useMutation({
    mutationFn: () =>
      api<LifecycleRequest>("/v1/accounts/export", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lifecycle"] }),
  });

  const deleteMut = useMutation({
    mutationFn: () =>
      api<LifecycleRequest>("/v1/accounts/delete", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lifecycle"] }),
  });

  const cancelMut = useMutation({
    mutationFn: () =>
      api<LifecycleRequest>("/v1/accounts/delete/cancel", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lifecycle"] }),
  });

  const pendingDelete = history.data?.find(
    (r) => r.kind === "DELETE" && r.status === "PENDING",
  );

  return (
    <section
      aria-labelledby="account-gdpr-heading"
      className="space-y-6 rounded-lg border border-border bg-card p-4 sm:p-6"
    >
      <header>
        <h2
          id="account-gdpr-heading"
          className="text-lg font-semibold tracking-tight"
        >
          {t("app.account.gdpr.title")}
        </h2>
      </header>

      {/* Data export */}
      <article className="rounded-md border border-border p-4">
        <h3 className="font-medium">{t("app.account.gdpr.export.title")}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("app.account.gdpr.export.desc")}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            onClick={() => exportMut.mutate()}
            disabled={exportMut.isPending}
          >
            {exportMut.isPending
              ? t("common.loading")
              : t("app.account.gdpr.export.cta")}
          </Button>
          {exportMut.isSuccess ? (
            <span className="text-sm text-emerald-700">
              {t("app.account.gdpr.export.success")}
            </span>
          ) : null}
          {exportMut.isError ? (
            <span className="text-sm text-destructive">
              {t("app.account.gdpr.export.error")}
            </span>
          ) : null}
        </div>
      </article>

      {/* Account deletion */}
      <article className="rounded-md border border-border p-4">
        <h3 className="font-medium">{t("app.account.gdpr.delete.title")}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("app.account.gdpr.delete.desc")}
        </p>

        {pendingDelete ? (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <span>
              {interpolate(t("app.account.gdpr.delete.pending"), {
                date: formatDate(pendingDelete.purgeAfter),
              })}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => cancelMut.mutate()}
              disabled={cancelMut.isPending}
            >
              {cancelMut.isPending
                ? t("common.loading")
                : t("app.account.gdpr.delete.cancelCta")}
            </Button>
          </div>
        ) : (
          <DeleteRequestForm
            onConfirm={() => deleteMut.mutate()}
            isPending={deleteMut.isPending}
            isSuccess={deleteMut.isSuccess}
            isError={deleteMut.isError}
          />
        )}
        {cancelMut.isSuccess ? (
          <p className="mt-3 text-sm text-emerald-700">
            {t("app.account.gdpr.delete.cancelled")}
          </p>
        ) : null}
        {cancelMut.isError ? (
          <p className="mt-3 text-sm text-destructive">
            {t("app.account.gdpr.delete.error")}
          </p>
        ) : null}
      </article>

      {/* Lifecycle history */}
      <article className="rounded-md border border-border p-4">
        <h3 className="font-medium">{t("app.account.gdpr.history.title")}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("app.account.gdpr.history.desc")}
        </p>
        {history.isLoading ? (
          <p className="mt-3 text-sm text-muted-foreground">
            {t("common.loading")}
          </p>
        ) : null}
        {history.isError ? (
          <p className="mt-3 text-sm text-destructive">
            {t("common.error")}: {describeError(history.error)}
          </p>
        ) : null}
        {history.data && history.data.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            {t("app.account.gdpr.history.empty")}
          </p>
        ) : null}
        {history.data && history.data.length > 0 ? (
          <ul className="mt-3 divide-y divide-border text-sm">
            {history.data.map((entry) => (
              <li
                key={entry.id}
                className="flex flex-wrap items-center justify-between gap-2 py-2"
              >
                <span className="flex items-center gap-2">
                  <span className="font-medium">
                    {t(`app.account.gdpr.kind.${entry.kind}`)}
                  </span>
                  <span className="text-muted-foreground">
                    {formatDate(entry.requestedAt)}
                  </span>
                </span>
                <StatusBadge status={entry.status} />
              </li>
            ))}
          </ul>
        ) : null}
      </article>
    </section>
  );
}

/* ---------- Confirm dialog (2-step delete per ADR-024) ------------------ */

function DeleteRequestForm({
  onConfirm,
  isPending,
  isSuccess,
  isError,
}: {
  onConfirm: () => void;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
}) {
  const t = useTranslations();
  const [open, setOpen] = useState(false);

  // Closing the dialog after a confirm gives the polled history time to
  // refetch — we don't want the dialog to stay open while the row appears
  // upstream.
  const handleConfirm = () => {
    onConfirm();
    setOpen(false);
  };

  return (
    <div className="mt-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
          {/*
            Step 1 (per ADR-024): user clicks the destructive CTA, which
            opens a confirmation dialog rather than firing the mutation.
            Step 2 is the explicit "Yes, delete" inside the dialog —
            it's the second physical click that triggers the soft-delete.
          */}
          <DialogPrimitive.Trigger asChild>
            <Button variant="destructive">
              {t("app.account.gdpr.delete.cta")}
            </Button>
          </DialogPrimitive.Trigger>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/20 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0" />
            <DialogPrimitive.Content
              className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-6 shadow-lg outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95"
            >
              <DialogPrimitive.Title className="text-base font-semibold">
                {t("app.account.gdpr.delete.confirm.title")}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="mt-2 text-sm text-muted-foreground">
                {t("app.account.gdpr.delete.confirm.desc")}
              </DialogPrimitive.Description>
              <div className="mt-6 flex justify-end gap-2">
                <DialogPrimitive.Close asChild>
                  <Button variant="outline">
                    {t("app.account.gdpr.delete.confirm.cancel")}
                  </Button>
                </DialogPrimitive.Close>
                <Button
                  variant="destructive"
                  onClick={handleConfirm}
                  disabled={isPending}
                >
                  {isPending
                    ? t("common.loading")
                    : t("app.account.gdpr.delete.confirm.confirm")}
                </Button>
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
        {isSuccess ? (
          <span className="text-sm text-emerald-700">
            {t("app.account.gdpr.delete.success")}
          </span>
        ) : null}
        {isError ? (
          <span className="text-sm text-destructive">
            {t("app.account.gdpr.delete.error")}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/* ---------- Status badge ----------------------------------------------- */

function StatusBadge({ status }: { status: LifecycleStatus }) {
  const t = useTranslations();
  const tone = STATUS_TONES[status];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {t(`app.account.gdpr.status.${status}`)}
    </span>
  );
}

const STATUS_TONES: Record<LifecycleStatus, string> = {
  PENDING: "border-amber-300 bg-amber-50 text-amber-900",
  COMPLETED: "border-emerald-300 bg-emerald-50 text-emerald-900",
  CANCELLED: "border-border bg-muted text-muted-foreground",
  FAILED: "border-destructive/30 bg-destructive/10 text-destructive",
};

/* ---------- helpers ----------------------------------------------------- */

function interpolate(
  template: string,
  values: Record<string, string>,
): string {
  return template.replace(/\{(\w+)\}/g, (_match, key: string) => {
    const replacement = values[key];
    return replacement ?? `{${key}}`;
  });
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    return `HTTP ${error.status}`;
  }
  if (error instanceof Error) return error.message;
  return String(error);
}
